import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, Save, X, AlertTriangle, Search,
  ArrowLeft, BookOpen, RefreshCw
} from "lucide-react";

export default function TermBaseManager({ project }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [inconsistencies, setInconsistencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newTerm, setNewTerm] = useState({ source_term: '', target_term: '', domain: '', notes: '' });
  const [editTerm, setEditTerm] = useState({});
  const [showInconsistencies, setShowInconsistencies] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [termsRes, inconRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/termbase`),
        axios.get(`${API}/projects/${projectId}/termbase/inconsistencies`),
      ]);
      setTerms(termsRes.data);
      setInconsistencies(inconRes.data);
    } catch (e) {
      toast.error("Failed to load term base");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) fetchData();
  }, [projectId, fetchData]);

  const handleAdd = useCallback(async () => {
    if (!newTerm.source_term.trim() || !newTerm.target_term.trim()) {
      toast.error("Source and target terms are required");
      return;
    }
    try {
      const res = await axios.post(`${API}/projects/${projectId}/termbase`, newTerm);
      setTerms(prev => [...prev, res.data]);
      setNewTerm({ source_term: '', target_term: '', domain: '', notes: '' });
      setShowAdd(false);
      toast.success("Term added");
    } catch (e) {
      toast.error("Failed to add term");
    }
  }, [projectId, newTerm]);

  const handleUpdate = useCallback(async (termId) => {
    try {
      const res = await axios.put(`${API}/projects/${projectId}/termbase/${termId}`, editTerm);
      setTerms(prev => prev.map(t => t.id === termId ? res.data : t));
      setEditingId(null);
      setEditTerm({});
      toast.success("Term updated");
    } catch (e) {
      toast.error("Failed to update term");
    }
  }, [projectId, editTerm]);

  const handleDelete = useCallback(async (termId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/termbase/${termId}`);
      setTerms(prev => prev.filter(t => t.id !== termId));
      toast.success("Term deleted");
    } catch (e) {
      toast.error("Failed to delete term");
    }
  }, [projectId]);

  const filteredTerms = terms.filter(t =>
    !searchQuery ||
    t.source_term.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.target_term.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.domain && t.domain.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="termbase-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BookOpen className="w-6 h-6 text-neon-cyan" strokeWidth={1.5} />
            <h1 className="text-3xl font-heading font-bold tracking-tight uppercase text-[#E0E0E0]">
              Term Base
            </h1>
          </div>
          <p className="text-sm font-ui text-[#94A3B8]">
            {project?.name || 'Project'} &middot; {terms.length} entries
          </p>
        </div>
        <button
          onClick={() => navigate(`/editor/${projectId}`)}
          className="flex items-center gap-2 px-4 py-2 rounded-sm text-[#94A3B8] hover:text-[#E0E0E0] border border-[#2A2F3A] hover:border-[#4A5568] font-ui text-sm transition-colors duration-200"
          data-testid="back-to-editor-btn"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Editor
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm text-[#E0E0E0] font-mono placeholder:text-[#64748B] outline-none transition-colors duration-200"
            data-testid="term-search-input"
          />
        </div>

        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-2 px-4 py-2 rounded-sm bg-neon-cyan/10 hover:bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30 hover:border-neon-cyan/60 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200"
          data-testid="add-term-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          Add Term
        </button>

        {inconsistencies.length > 0 && (
          <button
            onClick={() => setShowInconsistencies(!showInconsistencies)}
            className="flex items-center gap-2 px-4 py-2 rounded-sm bg-neon-warning/10 text-neon-warning border border-neon-warning/30 font-ui text-sm font-medium transition-colors duration-200"
            data-testid="inconsistencies-btn"
          >
            <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
            {inconsistencies.length} Inconsistencies
          </button>
        )}

        <button
          onClick={fetchData}
          className="p-2 rounded-sm text-[#64748B] hover:text-[#E0E0E0] hover:bg-white/5 transition-colors duration-200"
          data-testid="refresh-terms-btn"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Add term form */}
      {showAdd && (
        <div className="glass-card p-4 mb-4" data-testid="add-term-form">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-ui uppercase tracking-wider text-[#64748B] mb-1 block">Source (ZH-CN)</label>
              <input
                type="text"
                value={newTerm.source_term}
                onChange={e => setNewTerm(prev => ({ ...prev, source_term: e.target.value }))}
                className="w-full px-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                placeholder="Chinese term"
                data-testid="new-term-source"
              />
            </div>
            <div>
              <label className="text-[10px] font-ui uppercase tracking-wider text-[#64748B] mb-1 block">Target (DE-DE)</label>
              <input
                type="text"
                value={newTerm.target_term}
                onChange={e => setNewTerm(prev => ({ ...prev, target_term: e.target.value }))}
                className="w-full px-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                placeholder="German translation"
                data-testid="new-term-target"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-ui uppercase tracking-wider text-[#64748B] mb-1 block">Domain</label>
              <input
                type="text"
                value={newTerm.domain}
                onChange={e => setNewTerm(prev => ({ ...prev, domain: e.target.value }))}
                className="w-full px-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                placeholder="e.g. UI, Stats, Items"
                data-testid="new-term-domain"
              />
            </div>
            <div>
              <label className="text-[10px] font-ui uppercase tracking-wider text-[#64748B] mb-1 block">Notes</label>
              <input
                type="text"
                value={newTerm.notes}
                onChange={e => setNewTerm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                placeholder="Optional notes"
                data-testid="new-term-notes"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAdd}
              className="px-4 py-2 rounded-sm bg-neon-green/10 text-neon-green border border-neon-green/30 font-ui text-sm font-semibold uppercase tracking-wider transition-colors duration-200"
              data-testid="save-new-term-btn"
            >
              Save Term
            </button>
            <button
              onClick={() => { setShowAdd(false); setNewTerm({ source_term: '', target_term: '', domain: '', notes: '' }); }}
              className="px-4 py-2 rounded-sm text-[#94A3B8] border border-[#2A2F3A] font-ui text-sm transition-colors duration-200"
              data-testid="cancel-new-term-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inconsistencies panel */}
      {showInconsistencies && inconsistencies.length > 0 && (
        <div className="glass-card p-4 mb-4 border-neon-warning/30" data-testid="inconsistencies-panel">
          <h3 className="font-heading font-semibold text-sm text-neon-warning uppercase tracking-wider mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Term Inconsistencies
          </h3>
          <div className="space-y-2 max-h-60 overflow-auto">
            {inconsistencies.map((inc, idx) => (
              <div key={idx} className="text-xs font-mono p-2 bg-surface-200 rounded-sm border border-[#2A2F3A]">
                <span className="text-neon-warning">"{inc.term_source}" → "{inc.term_target}"</span>
                <span className="text-[#64748B]"> not found in: </span>
                <span className="text-[#E0E0E0]">"{inc.segment_target}"</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms table */}
      <div className="glass-card overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_1fr_120px_1fr_80px] gap-3 px-4 py-2.5 border-b border-[#2A2F3A] text-[10px] font-ui font-medium uppercase tracking-wider text-[#64748B]">
          <span>Source (ZH-CN)</span>
          <span>Target (DE-DE)</span>
          <span>Domain</span>
          <span>Notes</span>
          <span>Actions</span>
        </div>

        {/* Terms */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTerms.length === 0 ? (
          <div className="py-12 text-center text-[#64748B] font-ui text-sm">
            {searchQuery ? 'No terms match your search.' : 'No terms in the term base yet.'}
          </div>
        ) : (
          <div className="divide-y divide-[#2A2F3A]/30">
            {filteredTerms.map(term => (
              <div key={term.id} className="group" data-testid={`term-row-${term.id}`}>
                {editingId === term.id ? (
                  <div className="grid grid-cols-[1fr_1fr_120px_1fr_80px] gap-3 px-4 py-2.5 items-center bg-surface-200/50">
                    <input
                      value={editTerm.source_term ?? term.source_term}
                      onChange={e => setEditTerm(prev => ({ ...prev, source_term: e.target.value }))}
                      className="px-2 py-1 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                      data-testid="edit-term-source"
                    />
                    <input
                      value={editTerm.target_term ?? term.target_term}
                      onChange={e => setEditTerm(prev => ({ ...prev, target_term: e.target.value }))}
                      className="px-2 py-1 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                      data-testid="edit-term-target"
                    />
                    <input
                      value={editTerm.domain ?? term.domain}
                      onChange={e => setEditTerm(prev => ({ ...prev, domain: e.target.value }))}
                      className="px-2 py-1 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                      data-testid="edit-term-domain"
                    />
                    <input
                      value={editTerm.notes ?? term.notes}
                      onChange={e => setEditTerm(prev => ({ ...prev, notes: e.target.value }))}
                      className="px-2 py-1 bg-surface-100 border border-[#2A2F3A] focus:border-neon-cyan rounded-sm text-sm font-mono text-[#E0E0E0] outline-none"
                      data-testid="edit-term-notes"
                    />
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleUpdate(term.id)} className="p-1.5 rounded-sm bg-neon-green/10 text-neon-green" data-testid="save-term-edit-btn">
                        <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => { setEditingId(null); setEditTerm({}); }} className="p-1.5 rounded-sm bg-neon-error/10 text-neon-error" data-testid="cancel-term-edit-btn">
                        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-[1fr_1fr_120px_1fr_80px] gap-3 px-4 py-2.5 items-center hover:bg-white/[0.02] transition-colors duration-200">
                    <span className="text-sm font-mono text-[#E0E0E0]">{term.source_term}</span>
                    <span className="text-sm font-mono text-neon-green">{term.target_term}</span>
                    <span className="text-xs font-ui text-[#94A3B8]">{term.domain || '—'}</span>
                    <span className="text-xs font-ui text-[#64748B] truncate">{term.notes || '—'}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => { setEditingId(term.id); setEditTerm({ source_term: term.source_term, target_term: term.target_term, domain: term.domain, notes: term.notes }); }}
                        className="p-1.5 rounded-sm hover:bg-white/5 text-[#64748B] hover:text-[#E0E0E0]"
                        data-testid={`edit-term-${term.id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleDelete(term.id)}
                        className="p-1.5 rounded-sm hover:bg-neon-error/10 text-[#64748B] hover:text-neon-error"
                        data-testid={`delete-term-${term.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
